import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Bell, Lock, Palette, Plug, Copy, Check } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useIntegrations } from '../../hooks/useIntegrations.js';
import { NOTIFICATION_CATEGORIES, useNotificationPrefs } from '../../lib/notificationPrefs.js';
import { relativeTime } from '../../lib/format.js';
import { cn } from '@hts/ui';
import type { Theme } from '../../lib/theme.js';
import { CompanySettingsCard } from './CompanySettingsCard.js';
import { CallIntelSettingsCard } from './CallIntelSettingsCard.js';

/**
 * Settings: every value here is either a real, working preference (theme,
 * bell categories — stored per browser) or a read-only value loaded from the
 * API (tenant, role, ConnectWise connection, session lifetime). Nothing is a
 * placeholder control.
 */

const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner', admin: 'Admin', dispatcher: 'Dispatcher', tech: 'Technician', viewer: 'Viewer',
};

function formatDuration(seconds: number): string {
  if (seconds % 86400 === 0) return `${seconds / 86400} day${seconds === 86400 ? '' : 's'}`;
  if (seconds % 3600 === 0) return `${seconds / 3600} hour${seconds === 3600 ? '' : 's'}`;
  return `${Math.round(seconds / 60)} minutes`;
}

export function SettingsPage() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const auth = useAuth();
  const [prefs, setPref] = useNotificationPrefs();
  const user = auth.status === 'authenticated' ? auth.user : undefined;
  const tenant = user?.activeTenant ?? undefined;
  const role = user?.memberships.find(m => m.tenantId === user.activeTenantId)?.role;
  const isAdmin = role === 'admin' || role === 'owner';

  // Integrations API is admin/owner only.
  const integrations = useIntegrations();
  const cw = (integrations.data ?? []).find(c => c.kind === 'connectwise');

  return (
    <div className="p-6 max-w-2xl space-y-8">
      <div>
        <h1 className="text-xl font-bold text-[hsl(var(--content))]">Settings</h1>
        <p className="text-sm text-[hsl(var(--content-subtle))] mt-0.5">Workspace details and your display preferences</p>
      </div>

      <CompanySettingsCard editable={isAdmin} />

      <CallIntelSettingsCard editable={isAdmin} />

      {/* Appearance */}
      <SettingSection icon={Palette} title="Appearance">
        <SettingRow label="Theme" description="Color scheme for this browser">
          <div className="flex p-1 gap-1 bg-[hsl(var(--surface-subtle))] rounded-xl border border-[hsl(var(--border))]" role="radiogroup" aria-label="Theme">
            {(['light', 'dark', 'system'] as Theme[]).map(t => (
              <button
                key={t}
                role="radio"
                aria-checked={theme === t}
                onClick={() => setTheme(t)}
                className={cn(
                  'px-3 py-1 rounded-lg text-xs font-medium capitalize transition-colors',
                  theme === t
                    ? 'bg-[hsl(var(--surface))] text-[hsl(var(--content))] shadow-sm'
                    : 'text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))]'
                )}
              >
                {t}
              </button>
            ))}
          </div>
        </SettingRow>
      </SettingSection>

      {/* Notifications */}
      <SettingSection icon={Bell} title="Notifications" subtitle="What appears in the header bell on this browser. Feeds are live from tickets, dispatch, calls and integration health.">
        {NOTIFICATION_CATEGORIES.map(c => (
          <SettingRow key={c.key} label={c.label} description={c.description}>
            <Toggle label={c.label} checked={prefs[c.key]} onChange={v => setPref(c.key, v)} />
          </SettingRow>
        ))}
      </SettingSection>

      {/* Tenant */}
      <SettingSection icon={Globe} title="Workspace">
        <SettingRow label="Workspace" description="Organization whose data and settings are shown">
          {user && user.memberships.length > 1 ? (
            <select aria-label="Active workspace" value={user.activeTenantId ?? ''} onChange={(e) => void auth.switchTenant(e.target.value)} className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-[hsl(var(--content))]">
              {user.memberships.map((m) => <option key={m.tenantId} value={m.tenantId}>{m.tenant?.name ?? m.tenantId}{m.tenant?.slug ? ` (${m.tenant.slug})` : ''}</option>)}
            </select>
          ) : <span className="text-sm text-[hsl(var(--content))]">{tenant?.name ?? '—'}</span>}
        </SettingRow>
        <SettingRow label="Tenant slug" description="Short identifier">
          <Mono>{tenant?.slug ?? '—'}</Mono>
        </SettingRow>
        <SettingRow label="Tenant ID" description="Unique identifier (for support requests)">
          {tenant?.id ? <CopyValue value={tenant.id} /> : <Mono>—</Mono>}
        </SettingRow>
        <SettingRow label="Your role" description="Managed by admins under Users & Roles">
          <span className="text-sm text-[hsl(var(--content))]">{role ? ROLE_LABEL[role] ?? role : '—'}</span>
        </SettingRow>
      </SettingSection>

      {/* ConnectWise */}
      <SettingSection icon={Plug} title="ConnectWise">
        {!user ? (
          <SettingRow label="Connection" description="Loading…">
            <span className="text-sm text-[hsl(var(--content-subtle))]">…</span>
          </SettingRow>
        ) : !isAdmin ? (
          <SettingRow label="Connection" description="Integration details are visible to admins and owners">
            <span className="text-sm text-[hsl(var(--content-subtle))]">Restricted</span>
          </SettingRow>
        ) : integrations.isLoading ? (
          <SettingRow label="Connection" description="Loading…">
            <span className="text-sm text-[hsl(var(--content-subtle))]">…</span>
          </SettingRow>
        ) : integrations.isError ? (
          <SettingRow label="Connection" description="Couldn't load integrations">
            <button onClick={() => integrations.refetch()} className="text-sm text-brand-600 dark:text-brand-400 hover:underline">Retry</button>
          </SettingRow>
        ) : (
          <>
            <SettingRow label="Status" description="ConnectWise Manage API connection">
              <span className={cn('text-sm font-medium', cw?.status === 'connected' ? 'text-green-600 dark:text-green-400' : cw?.status === 'error' ? 'text-red-600 dark:text-red-400' : 'text-[hsl(var(--content-subtle))]')}>
                {cw ? cw.status.charAt(0).toUpperCase() + cw.status.slice(1) : 'Not connected'}
              </span>
            </SettingRow>
            {cw?.config?.companyId && (
              <SettingRow label="Company ID" description="ConnectWise company identifier">
                <Mono>{cw.config.companyId}</Mono>
              </SettingRow>
            )}
            {cw?.config?.baseUrl && (
              <SettingRow label="API base URL" description="ConnectWise site">
                <Mono>{cw.config.baseUrl}</Mono>
              </SettingRow>
            )}
            {cw && (
              <SettingRow label="Last successful sync" description="Worker ticket/member/board sync">
                <span className="text-sm text-[hsl(var(--content))]" title={cw.lastSyncAt ? new Date(cw.lastSyncAt).toLocaleString() : undefined}>
                  {cw.lastSyncAt ? relativeTime(cw.lastSyncAt) : 'Never'}
                </span>
              </SettingRow>
            )}
            <SettingRow label="Manage" description="Credentials, testing and rotation">
              <button
                onClick={() => navigate('/admin/integrations')}
                className="px-3 py-1.5 rounded-lg border border-[hsl(var(--border))] text-sm text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] transition-colors"
              >
                Open Integrations
              </button>
            </SettingRow>
          </>
        )}
      </SettingSection>

      {/* Security (read-only: identity is owned by Entra ID) */}
      <SettingSection icon={Lock} title="Security">
        <SettingRow label="Sign-in" description="Single sign-on with your company Microsoft account">
          <span className="text-sm text-[hsl(var(--content))]">Microsoft Entra ID</span>
        </SettingRow>
        <SettingRow label="Multi-factor authentication" description="MFA and conditional access are enforced by your Entra ID tenant policies, not by TechSys Connect">
          <span className="text-sm text-[hsl(var(--content-subtle))]">Managed in Entra ID</span>
        </SettingRow>
        <SettingRow label="Session lifetime" description="You are signed out automatically after this period">
          <span className="text-sm text-[hsl(var(--content))]">
            {user?.sessionTtlSeconds ? formatDuration(user.sessionTtlSeconds) : '—'}
          </span>
        </SettingRow>
        <SettingRow label="Sign out" description={user ? `Signed in as ${user.email}` : ''}>
          <button
            onClick={async () => { await auth.signOut(); navigate('/login'); }}
            className="px-3 py-1.5 rounded-lg border border-red-300 dark:border-red-800 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            Sign out
          </button>
        </SettingRow>
      </SettingSection>
    </div>
  );
}

function Mono({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-mono text-xs bg-[hsl(var(--surface-subtle))] border border-[hsl(var(--border))] rounded px-2 py-1 text-[hsl(var(--content))] break-all">
      {children}
    </span>
  );
}

function CopyValue({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <span className="flex items-center gap-1.5">
      <Mono>{value}</Mono>
      <button
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            // clipboard blocked; value is still selectable
          }
        }}
        className="p-1 rounded text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))]"
        aria-label="Copy tenant ID"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </span>
  );
}

function SettingSection({ icon: Icon, title, subtitle, children }: { icon: React.ElementType; title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-[hsl(var(--border))]">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-[hsl(var(--content-subtle))]" />
          <h3 className="text-sm font-semibold text-[hsl(var(--content))]">{title}</h3>
        </div>
        {subtitle && <p className="text-xs text-[hsl(var(--content-subtle))] mt-1">{subtitle}</p>}
      </div>
      <div className="divide-y divide-[hsl(var(--border))]">{children}</div>
    </div>
  );
}

function SettingRow({ label, description, children }: { label: string; description: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-4">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[hsl(var(--content))]">{label}</p>
        {description && <p className="text-xs text-[hsl(var(--content-subtle))] mt-0.5">{description}</p>}
      </div>
      <div className="shrink-0 max-w-[60%]">{children}</div>
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2',
        checked ? 'bg-brand-600' : 'bg-gray-300 dark:bg-gray-600'
      )}
      role="switch"
      aria-checked={checked}
      aria-label={label}
    >
      <span className={cn(
        'inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
        checked ? 'translate-x-5' : 'translate-x-0'
      )} />
    </button>
  );
}
